/**
 * Central reports registry — M3X.UIPOLISH PR 10-14 (Sub-domain B).
 *
 * Per spec §1.B + Definition of Done #6: "Reports library at
 * /reports shows 150+ reports across all domains + cadences +
 * personas + cuts."
 *
 * Single source of truth for every report Solvait exposes. Domain
 * index pages + Browse-by-{cadence,persona,cut} views + Global
 * Search ⌘K (PR 29) all consume this catalog.
 *
 * Each entry's title key is bilingual (i18n key resolved at render).
 * The `to` URL points at the existing report page; for reports
 * whose dedicated page hasn't landed, the URL deep-links into the
 * domain library page with a hash filter (e.g.
 * /reports/leaves#sick-leave-pattern).
 *
 * Spec scope estimate: Loans 25 + Leaves 30 + EOS 30 + Attendance
 * 30 + Payroll 38 + Cross-pack 10 = ~163. This file ships the
 * catalog with the canonical reports per pack (the per-pack spec
 * names them in §1.20 / §1.18 / §1.24 / §1.F.2). Gaps surface as
 * a finding (per Q2 lock — built, not deferred).
 */

import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  BadgeDollarSign,
  Banknote,
  Calendar,
  CalendarDays,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Gauge,
  GitBranch,
  Layers,
  Network,
  Package,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  UploadCloud,
  Users,
} from "lucide-react";

import type { ReportDomain } from "../components/ui/ReportPage";

export type ReportCadence =
  | "real_time"
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annual"
  | "on_demand";

export type ReportPersona =
  | "hr_director"
  | "hr_partner"
  | "manager"
  | "finance"
  | "compliance_officer"
  | "ceo_chro"
  | "employee";

export type ReportCut =
  | "per_employee"
  | "per_department"
  | "per_project"
  | "per_grade"
  | "per_branch"
  | "per_nationality"
  | "cross_le"
  | "global";

export interface ReportDef {
  /** Stable internal ID — for registry lookup. Never visible to users. */
  id: string;
  /** Display key (bilingual via t). */
  titleKey: string;
  /** 1-line description key. */
  descriptionKey: string;
  /** Domain — drives brand color strip in <ReportPage />. */
  domain: ReportDomain;
  /** Where the report renders. */
  to: string;
  /** Lucide icon for card display. */
  icon: LucideIcon;
  /** When this report updates. */
  cadence: ReportCadence;
  /** Who reads it. */
  persona: ReportPersona;
  /** What it cuts by. */
  cut: ReportCut;
  /** True when the dedicated page hasn't landed; library still
   * surfaces the card with a coming-soon badge. */
  comingSoon?: boolean;
}

/**
 * The locked catalog. Each entry is named per the spec section
 * for its pack. Bilingual titles use the `reports_catalog.*` i18n
 * namespace; English fallback is provided inline via t(key, fallback).
 */
export const REPORTS_REGISTRY: ReportDef[] = [
  // ===========================================================================
  // LOANS — per M3X.LOANS §1.20 (25 reports)
  // ===========================================================================
  {
    id: "loans_portfolio_summary",
    titleKey: "reports_catalog.loans_portfolio",
    descriptionKey: "reports_catalog.loans_portfolio_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: CreditCard,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "loans_active_by_employee",
    titleKey: "reports_catalog.loans_by_employee",
    descriptionKey: "reports_catalog.loans_by_employee_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: Users,
    cadence: "real_time",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "loans_arrears",
    titleKey: "reports_catalog.loans_arrears",
    descriptionKey: "reports_catalog.loans_arrears_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: AlertCircle,
    cadence: "weekly",
    persona: "hr_director",
    cut: "per_employee",
  },
  {
    id: "loans_article_90_firings",
    titleKey: "reports_catalog.loans_article_90",
    descriptionKey: "reports_catalog.loans_article_90_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "loans_article_95_garnishments",
    titleKey: "reports_catalog.loans_article_95",
    descriptionKey: "reports_catalog.loans_article_95_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "loans_refinance_candidates",
    titleKey: "reports_catalog.loans_refinance",
    descriptionKey: "reports_catalog.loans_refinance_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: Sparkles,
    cadence: "quarterly",
    persona: "hr_director",
    cut: "per_employee",
  },
  {
    id: "loans_by_product",
    titleKey: "reports_catalog.loans_by_product",
    descriptionKey: "reports_catalog.loans_by_product_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: Package,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "loans_by_department",
    titleKey: "reports_catalog.loans_by_department",
    descriptionKey: "reports_catalog.loans_by_department_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: Layers,
    cadence: "monthly",
    persona: "manager",
    cut: "per_department",
  },
  {
    id: "loans_sharia_structure",
    titleKey: "reports_catalog.loans_sharia",
    descriptionKey: "reports_catalog.loans_sharia_desc",
    domain: "loans",
    to: "/reports/loans",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },

  // ===========================================================================
  // LEAVES — per M3X.LEAVES §1.18 (30 reports)
  // ===========================================================================
  {
    id: "leaves_balances",
    titleKey: "reports_catalog.leaves_balances",
    descriptionKey: "reports_catalog.leaves_balances_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: CalendarDays,
    cadence: "real_time",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_encashment",
    titleKey: "reports_catalog.leaves_encashment",
    descriptionKey: "reports_catalog.leaves_encashment_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Banknote,
    cadence: "monthly",
    persona: "finance",
    cut: "per_employee",
  },
  {
    id: "leaves_accruals",
    titleKey: "reports_catalog.leaves_accruals",
    descriptionKey: "reports_catalog.leaves_accruals_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: TrendingUp,
    cadence: "monthly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_by_type",
    titleKey: "reports_catalog.leaves_by_type",
    descriptionKey: "reports_catalog.leaves_by_type_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Layers,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "leaves_sick_pattern",
    titleKey: "reports_catalog.leaves_sick_pattern",
    descriptionKey: "reports_catalog.leaves_sick_pattern_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Sparkles,
    cadence: "weekly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_carryover_aging",
    titleKey: "reports_catalog.leaves_carryover",
    descriptionKey: "reports_catalog.leaves_carryover_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Clock,
    cadence: "monthly",
    persona: "hr_director",
    cut: "per_employee",
  },
  {
    id: "leaves_pay_in_advance",
    titleKey: "reports_catalog.leaves_pay_advance",
    descriptionKey: "reports_catalog.leaves_pay_advance_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: BadgeDollarSign,
    cadence: "monthly",
    persona: "finance",
    cut: "per_employee",
  },
  {
    id: "leaves_no_ticket",
    titleKey: "reports_catalog.leaves_no_ticket",
    descriptionKey: "reports_catalog.leaves_no_ticket_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: AlertCircle,
    cadence: "monthly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_custody_handover",
    titleKey: "reports_catalog.leaves_custody",
    descriptionKey: "reports_catalog.leaves_custody_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: ShieldAlert,
    cadence: "real_time",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_visa_window",
    titleKey: "reports_catalog.leaves_visa_window",
    descriptionKey: "reports_catalog.leaves_visa_window_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: ShieldAlert,
    cadence: "weekly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_post_end_return",
    titleKey: "reports_catalog.leaves_post_end_return",
    descriptionKey: "reports_catalog.leaves_post_end_return_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Calendar,
    cadence: "daily",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "leaves_pending_sla",
    titleKey: "reports_catalog.leaves_pending_sla",
    descriptionKey: "reports_catalog.leaves_pending_sla_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: AlertCircle,
    cadence: "daily",
    persona: "hr_partner",
    cut: "global",
  },
  {
    id: "leaves_denial_reasons",
    titleKey: "reports_catalog.leaves_denials",
    descriptionKey: "reports_catalog.leaves_denials_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: AlertCircle,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "leaves_overlap",
    titleKey: "reports_catalog.leaves_overlap",
    descriptionKey: "reports_catalog.leaves_overlap_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Network,
    cadence: "real_time",
    persona: "manager",
    cut: "per_department",
  },
  {
    id: "leaves_khidmat_originated",
    titleKey: "reports_catalog.leaves_khidmat",
    descriptionKey: "reports_catalog.leaves_khidmat_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: Sparkles,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "leaves_hr_escalations",
    titleKey: "reports_catalog.leaves_escalations",
    descriptionKey: "reports_catalog.leaves_escalations_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: AlertCircle,
    cadence: "weekly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "leaves_eos_residual",
    titleKey: "reports_catalog.leaves_eos_residual",
    descriptionKey: "reports_catalog.leaves_eos_residual_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: BadgeDollarSign,
    cadence: "monthly",
    persona: "finance",
    cut: "per_employee",
  },
  {
    id: "leaves_statutory_violations",
    titleKey: "reports_catalog.leaves_statutory",
    descriptionKey: "reports_catalog.leaves_statutory_desc",
    domain: "leaves",
    to: "/reports/leaves",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },

  // ===========================================================================
  // EOS — per M3X.EOS §1.24 (30 reports)
  // ===========================================================================
  {
    id: "eos_settlements_summary",
    titleKey: "reports_catalog.eos_settlements",
    descriptionKey: "reports_catalog.eos_settlements_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: Gauge,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "eos_by_trigger",
    titleKey: "reports_catalog.eos_by_trigger",
    descriptionKey: "reports_catalog.eos_by_trigger_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: Layers,
    cadence: "quarterly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "eos_article_80_subtypes",
    titleKey: "reports_catalog.eos_article_80",
    descriptionKey: "reports_catalog.eos_article_80_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "eos_article_81_disputed",
    titleKey: "reports_catalog.eos_article_81",
    descriptionKey: "reports_catalog.eos_article_81_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "eos_article_84_85_floors",
    titleKey: "reports_catalog.eos_article_84",
    descriptionKey: "reports_catalog.eos_article_84_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "eos_absconded",
    titleKey: "reports_catalog.eos_absconded",
    descriptionKey: "reports_catalog.eos_absconded_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: AlertCircle,
    cadence: "weekly",
    persona: "hr_director",
    cut: "per_employee",
  },
  {
    id: "eos_deceased",
    titleKey: "reports_catalog.eos_deceased",
    descriptionKey: "reports_catalog.eos_deceased_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: AlertCircle,
    cadence: "monthly",
    persona: "hr_director",
    cut: "per_employee",
  },
  {
    id: "eos_clearance_status",
    titleKey: "reports_catalog.eos_clearance",
    descriptionKey: "reports_catalog.eos_clearance_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: Eye,
    cadence: "daily",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "eos_government_filings",
    titleKey: "reports_catalog.eos_gov_filings",
    descriptionKey: "reports_catalog.eos_gov_filings_desc",
    domain: "eos",
    to: "/reports/eos",
    icon: FileText,
    cadence: "daily",
    persona: "hr_partner",
    cut: "global",
  },

  // ===========================================================================
  // ATTENDANCE — per M3X.ATTENDANCE §1.F.2 (30 reports)
  // ===========================================================================
  {
    id: "attendance_late_summary",
    titleKey: "reports_catalog.attendance_late",
    descriptionKey: "reports_catalog.attendance_late_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: Clock,
    cadence: "weekly",
    persona: "manager",
    cut: "per_employee",
  },
  {
    id: "attendance_overtime",
    titleKey: "reports_catalog.attendance_overtime",
    descriptionKey: "reports_catalog.attendance_overtime_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: TrendingUp,
    cadence: "weekly",
    persona: "manager",
    cut: "per_department",
  },
  {
    id: "attendance_excuses",
    titleKey: "reports_catalog.attendance_excuses",
    descriptionKey: "reports_catalog.attendance_excuses_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: FileText,
    cadence: "monthly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "attendance_shift_compliance",
    titleKey: "reports_catalog.attendance_shift",
    descriptionKey: "reports_catalog.attendance_shift_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: ShieldAlert,
    cadence: "weekly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "attendance_article_107",
    titleKey: "reports_catalog.attendance_article_107",
    descriptionKey: "reports_catalog.attendance_article_107_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "attendance_article_108_women",
    titleKey: "reports_catalog.attendance_article_108",
    descriptionKey: "reports_catalog.attendance_article_108_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: ShieldAlert,
    cadence: "weekly",
    persona: "compliance_officer",
    cut: "per_employee",
    comingSoon: true,
  },
  {
    id: "attendance_wfh",
    titleKey: "reports_catalog.attendance_wfh",
    descriptionKey: "reports_catalog.attendance_wfh_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: Network,
    cadence: "monthly",
    persona: "hr_director",
    cut: "per_employee",
    comingSoon: true,
  },
  {
    id: "attendance_biometric_drift",
    titleKey: "reports_catalog.attendance_biometric",
    descriptionKey: "reports_catalog.attendance_biometric_desc",
    domain: "attendance",
    to: "/reports/attendance",
    icon: Eye,
    cadence: "daily",
    persona: "hr_partner",
    cut: "global",
    comingSoon: true,
  },

  // ===========================================================================
  // PAYROLL — per M3X.PAYROLL §1.F.2 (38 reports)
  // ===========================================================================
  {
    id: "payroll_salary_sheet_full",
    titleKey: "reports_catalog.payroll_salary_sheet",
    descriptionKey: "reports_catalog.payroll_salary_sheet_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: Banknote,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "payroll_salary_sheet_by_dept",
    titleKey: "reports_catalog.payroll_salary_dept",
    descriptionKey: "reports_catalog.payroll_salary_dept_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: Layers,
    cadence: "monthly",
    persona: "manager",
    cut: "per_department",
  },
  {
    id: "payroll_salary_sheet_by_project",
    titleKey: "reports_catalog.payroll_salary_project",
    descriptionKey: "reports_catalog.payroll_salary_project_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: GitBranch,
    cadence: "monthly",
    persona: "finance",
    cut: "per_project",
  },
  {
    id: "payroll_salary_sheet_by_grade",
    titleKey: "reports_catalog.payroll_salary_grade",
    descriptionKey: "reports_catalog.payroll_salary_grade_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: BadgeDollarSign,
    cadence: "monthly",
    persona: "hr_director",
    cut: "per_grade",
  },
  {
    id: "payroll_salary_sheet_by_nationality",
    titleKey: "reports_catalog.payroll_salary_nationality",
    descriptionKey: "reports_catalog.payroll_salary_nationality_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: Users,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_nationality",
  },
  {
    id: "payroll_mom_comparison",
    titleKey: "reports_catalog.payroll_mom",
    descriptionKey: "reports_catalog.payroll_mom_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: TrendingUp,
    cadence: "monthly",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "payroll_qoq_comparison",
    titleKey: "reports_catalog.payroll_qoq",
    descriptionKey: "reports_catalog.payroll_qoq_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: TrendingUp,
    cadence: "quarterly",
    persona: "ceo_chro",
    cut: "global",
  },
  {
    id: "payroll_yoy_comparison",
    titleKey: "reports_catalog.payroll_yoy",
    descriptionKey: "reports_catalog.payroll_yoy_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: TrendingUp,
    cadence: "annual",
    persona: "ceo_chro",
    cut: "global",
  },
  {
    id: "payroll_new_joiners",
    titleKey: "reports_catalog.payroll_new_joiners",
    descriptionKey: "reports_catalog.payroll_new_joiners_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: Users,
    cadence: "monthly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "payroll_not_appearing",
    titleKey: "reports_catalog.payroll_not_appearing",
    descriptionKey: "reports_catalog.payroll_not_appearing_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: AlertCircle,
    cadence: "monthly",
    persona: "hr_partner",
    cut: "per_employee",
  },
  {
    id: "payroll_gosi_reconciliation",
    titleKey: "reports_catalog.payroll_gosi_recon",
    descriptionKey: "reports_catalog.payroll_gosi_recon_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "payroll_wps_reconciliation",
    titleKey: "reports_catalog.payroll_wps_recon",
    descriptionKey: "reports_catalog.payroll_wps_recon_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "finance",
    cut: "global",
  },
  {
    id: "payroll_zatca_z25",
    titleKey: "reports_catalog.payroll_zatca",
    descriptionKey: "reports_catalog.payroll_zatca_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: FileText,
    cadence: "monthly",
    persona: "finance",
    cut: "global",
  },
  {
    id: "payroll_article_90_ledger",
    titleKey: "reports_catalog.payroll_article_90",
    descriptionKey: "reports_catalog.payroll_article_90_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "payroll_article_95_ledger",
    titleKey: "reports_catalog.payroll_article_95",
    descriptionKey: "reports_catalog.payroll_article_95_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: ShieldAlert,
    cadence: "monthly",
    persona: "compliance_officer",
    cut: "per_employee",
  },
  {
    id: "payroll_ot_ledger",
    titleKey: "reports_catalog.payroll_ot_ledger",
    descriptionKey: "reports_catalog.payroll_ot_ledger_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: TrendingUp,
    cadence: "monthly",
    persona: "manager",
    cut: "per_employee",
  },
  {
    id: "payroll_loan_ledger",
    titleKey: "reports_catalog.payroll_loan_ledger",
    descriptionKey: "reports_catalog.payroll_loan_ledger_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: CreditCard,
    cadence: "monthly",
    persona: "finance",
    cut: "per_employee",
  },
  {
    id: "payroll_forecast_next_month",
    titleKey: "reports_catalog.payroll_forecast",
    descriptionKey: "reports_catalog.payroll_forecast_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: Sparkles,
    cadence: "monthly",
    persona: "ceo_chro",
    cut: "global",
  },
  {
    id: "payroll_anomalies",
    titleKey: "reports_catalog.payroll_anomalies",
    descriptionKey: "reports_catalog.payroll_anomalies_desc",
    domain: "payroll",
    to: "/operations/payroll",
    icon: AlertCircle,
    cadence: "real_time",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "payroll_year_end",
    titleKey: "reports_catalog.payroll_year_end",
    descriptionKey: "reports_catalog.payroll_year_end_desc",
    domain: "payroll",
    to: "/me/loans",
    icon: FileText,
    cadence: "annual",
    persona: "employee",
    cut: "per_employee",
  },
  {
    id: "payroll_salary_certificate",
    titleKey: "reports_catalog.payroll_salary_certificate",
    descriptionKey: "reports_catalog.payroll_salary_certificate_desc",
    domain: "payroll",
    to: "/me/loans",
    icon: FileText,
    cadence: "on_demand",
    persona: "employee",
    cut: "per_employee",
  },
  {
    id: "payroll_payslip_on_demand",
    titleKey: "reports_catalog.payroll_payslip",
    descriptionKey: "reports_catalog.payroll_payslip_desc",
    domain: "payroll",
    to: "/me/loans",
    icon: FileText,
    cadence: "on_demand",
    persona: "employee",
    cut: "per_employee",
  },

  // ===========================================================================
  // COMPLIANCE / Cross-pack — Mutaba'aa per-article + AI Insights
  // ===========================================================================
  {
    id: "compliance_headcount_dashboard",
    titleKey: "reports_catalog.headcount_dashboard",
    descriptionKey: "reports_catalog.headcount_dashboard_desc",
    domain: "headcount",
    to: "/reports/headcount",
    icon: Users,
    cadence: "real_time",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "compliance_turnover_dashboard",
    titleKey: "reports_catalog.turnover_dashboard",
    descriptionKey: "reports_catalog.turnover_dashboard_desc",
    domain: "headcount",
    to: "/reports/turnover",
    icon: TrendingUp,
    cadence: "monthly",
    persona: "ceo_chro",
    cut: "global",
  },
  {
    id: "compliance_compensation_dashboard",
    titleKey: "reports_catalog.compensation_dashboard",
    descriptionKey: "reports_catalog.compensation_dashboard_desc",
    domain: "headcount",
    to: "/reports/compensation",
    icon: BadgeDollarSign,
    cadence: "quarterly",
    persona: "ceo_chro",
    cut: "global",
  },
  {
    id: "compliance_audit_dashboard",
    titleKey: "reports_catalog.compliance_dashboard",
    descriptionKey: "reports_catalog.compliance_dashboard_desc",
    domain: "compliance",
    to: "/reports/compliance",
    icon: ShieldAlert,
    cadence: "real_time",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "ai_activity_log",
    titleKey: "reports_catalog.ai_activity",
    descriptionKey: "reports_catalog.ai_activity_desc",
    domain: "ai_insights",
    to: "/settings/audit-log?actor=ai",
    icon: Sparkles,
    cadence: "real_time",
    persona: "hr_director",
    cut: "global",
  },
  {
    id: "ai_stuck_inbox",
    titleKey: "reports_catalog.ai_stuck",
    descriptionKey: "reports_catalog.ai_stuck_desc",
    domain: "ai_insights",
    to: "/ai/stuck-inbox",
    icon: AlertCircle,
    cadence: "real_time",
    persona: "hr_partner",
    cut: "global",
  },
  {
    id: "mc_pending_approvals",
    titleKey: "reports_catalog.mc_pending",
    descriptionKey: "reports_catalog.mc_pending_desc",
    domain: "ai_insights",
    to: "/operations/approvals",
    icon: AlertCircle,
    cadence: "real_time",
    persona: "manager",
    cut: "global",
  },
  {
    id: "mc_compliance_alerts",
    titleKey: "reports_catalog.mc_compliance_alerts",
    descriptionKey: "reports_catalog.mc_compliance_alerts_desc",
    domain: "compliance",
    to: "/people/compliance-alerts",
    icon: ShieldAlert,
    cadence: "real_time",
    persona: "compliance_officer",
    cut: "global",
  },
  {
    id: "mc_import_history",
    titleKey: "reports_catalog.mc_import_history",
    descriptionKey: "reports_catalog.mc_import_history_desc",
    domain: "ai_insights",
    to: "/settings/imports",
    icon: UploadCloud,
    cadence: "real_time",
    persona: "hr_partner",
    cut: "global",
  },
];

/**
 * Query helpers used by domain index pages + browse-by-* views.
 */
export function reportsByDomain(domain: ReportDomain): ReportDef[] {
  return REPORTS_REGISTRY.filter((r) => r.domain === domain);
}

export function reportsByCadence(cadence: ReportCadence): ReportDef[] {
  return REPORTS_REGISTRY.filter((r) => r.cadence === cadence);
}

export function reportsByPersona(persona: ReportPersona): ReportDef[] {
  return REPORTS_REGISTRY.filter((r) => r.persona === persona);
}

export function reportsByCut(cut: ReportCut): ReportDef[] {
  return REPORTS_REGISTRY.filter((r) => r.cut === cut);
}

export function findReport(id: string): ReportDef | undefined {
  return REPORTS_REGISTRY.find((r) => r.id === id);
}

export function searchReports(query: string): ReportDef[] {
  if (!query.trim()) return REPORTS_REGISTRY;
  const needle = query.toLowerCase();
  return REPORTS_REGISTRY.filter(
    (r) =>
      r.id.toLowerCase().includes(needle) ||
      r.titleKey.toLowerCase().includes(needle) ||
      r.descriptionKey.toLowerCase().includes(needle) ||
      r.domain.toLowerCase().includes(needle),
  );
}

/**
 * Stable export of report counts per pack — used by /reports
 * landing meta lines and Sub-domain F search index.
 */
export const REPORT_COUNTS = {
  loans: reportsByDomain("loans").length,
  leaves: reportsByDomain("leaves").length,
  eos: reportsByDomain("eos").length,
  attendance: reportsByDomain("attendance").length,
  payroll: reportsByDomain("payroll").length,
  compliance:
    reportsByDomain("compliance").length +
    reportsByDomain("headcount").length,
  ai_insights: reportsByDomain("ai_insights").length,
  total: REPORTS_REGISTRY.length,
};
